import React, { useEffect, useState } from "react";
import "./Dashboard.css";
import { DashboardResponse, VerticalData } from "./types";
import { getDashboardCounts } from "./api/dashboardService";
import PageHeader from "../../components/common/PageHeader";
import { useAuth } from "../../context/AuthContext";
import ChartBox from "./ChartBox";
import { VERTICALS } from "../../constants/verticals";
import { useNavigate } from "react-router-dom";

const Dashboard: React.FC = () => {
  const [data, setData] = useState<DashboardResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const role = user?.role;

  const isAdmin = role === "Admin" || role === "CompanyAdmin";
  const userVertical = user?.vertical_key;

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    try {
      const res = await getDashboardCounts();
      setData(res.data);
    } catch (err) {
      console.error("Dashboard error:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="p-4">Loading dashboard...</div>;
  if (!data) return null;

  /* ================= USER ROLE ================= */

  const verticalOrder = ["lixil", "glazing", "hunter_douglas"];

  /* ================= FILTER VERTICALS ================= */
  const allVerticals = data.verticals || {};

  const visibleVerticals: VerticalData[] = isAdmin
    ? verticalOrder.map(k => allVerticals[k]).filter(Boolean)
    : userVertical
      ? [allVerticals[userVertical]].filter(Boolean)
      : [];

  /* ================= SAFE SUMMARY ================= */
  const userVerticalData = userVertical
    ? allVerticals[userVertical]
    : null;

  const summary = isAdmin
    ? {
      total_enquiries: data.summary?.total_enquiries || 0,
      inquiries_won: data.summary?.inquiries_won || 0,
      quotation_pending: data.summary?.quotation_pending || 0,
      lost_enquiries: data.summary?.lost_enquiries || 0,
      today_activities: data.summary?.today_activities || 0,
      tomorrow_activities: data.summary?.tomorrow_activities || 0,
    }
    : {
      total_enquiries: userVerticalData?.inquiries || 0,
      inquiries_won: userVerticalData?.inquiries_won || 0,
      quotation_pending: userVerticalData?.quotation_pending || 0,
      lost_enquiries: userVerticalData?.lost_enquiries || 0,
      today_activities: userVerticalData?.today_activities || 0,
      tomorrow_activities: userVerticalData?.tomorrow_activities || 0,
    };

  const getVerticalKey = (verticalId: number) =>
    VERTICALS.find(v => v.id === verticalId)?.key || "";

  const getEmployeeGraphData = (verticalId: number) => {
    const verticalKey = getVerticalKey(verticalId);
    if (!verticalKey || !data.employee_vs_inquiries_graph) return [];
    return data.employee_vs_inquiries_graph[verticalKey] || [];
  };


  return (
    <>
      <PageHeader
        title="Dashboard"
        breadcrumbs={[{ label: "Home" }, { label: "Dashboard" }]}
      />

      <div className="dashboard">

        {/* ===== TOP STATS ===== */}
        <div className="stats-row">
          <StatCard color="orange" icon="fa-users" title="Total Inquiries" value={summary.total_enquiries} />
          <StatCard color="green" icon="fa-chart-line" title="Inquiries Won" value={summary.inquiries_won} />
          <StatCard color="blue" icon="fa-file-invoice" title="Quotation Pending" value={summary.quotation_pending} />
          <StatCard color="red" icon="fa-triangle-exclamation" title="Lost Inquiries" value={summary.lost_enquiries} />

        </div>

        {/* ===== VERTICAL CARDS ===== */}
        {isAdmin ? (
          <>
            <div className="dashboard-grid">
              {visibleVerticals.map(v => (
                <VerticalCard key={v.vertical_id} data={v} />
              ))}
            </div>

            <div className="charts-wrapper">
              {visibleVerticals.map(v => (
                <ChartBox
                  key={v.vertical_id}
                  verticalName={v.vertical_name}
                  verticalKey={getVerticalKey(v.vertical_id)}
                  employees={getEmployeeGraphData(v.vertical_id)}
                />
              ))}
            </div>
          </>
        ) : (
          <div className="vertical-dashboard-row">
            <div className="vertical-cards">
              {visibleVerticals.map(v => (
                <VerticalCard key={v.vertical_id} data={v} />
              ))}
            </div>

            <div className="vertical-chart">
              {visibleVerticals[0] && (
                <ChartBox
                  verticalName={visibleVerticals[0].vertical_name}
                  verticalKey={userVertical || ""}
                  employees={
                    userVertical
                      ? data.employee_vs_inquiries_graph?.[userVertical] || []
                      : []
                  }
                />
              )}
            </div>
          </div>
        )}




      </div>
    </>
  );
};

export default Dashboard;

/* ================= COMPONENTS ================= */

const StatCard = ({ color, icon, title, value }: any) => (
  <div className={`stat-card ${color}`}>
    <div className="stat-icon">
      <i className={`fas ${icon}`}></i>
    </div>
    <div>
      <div className="stat-title">{title}</div>
      <div className="stat-value">{value || 0}</div>
    </div>
  </div>
);

const VerticalCard = ({ data }: { data: VerticalData }) => {
  const navigate = useNavigate(); // ✅ here

  const goToList = (filter: any) => {
    const v = VERTICALS.find(x => x.id === data.vertical_id);
    if (!v) return;

    const params = new URLSearchParams(filter).toString();

    navigate(`/${v.key}/inquiries?${params}`);
  };
  return (
    <div className="info-card">
      <h3>{data.vertical_name}</h3>

      <div className="info-grid">
        <InfoBox color="blue" icon="fa-users" label="Inquiries" value={data.inquiries || 0} onClick={() => goToList({})} />
        <InfoBox color="green" icon="fa-chart-line" label="Inquiries Won" value={data.inquiries_won || 0} onClick={() => goToList({ stage: "won" })} />
        <InfoBox color="yellow" icon="fa-star" label="High Probability" value={data.high_probability || 0} onClick={() => goToList({ probability: "hot" })} />
        <InfoBox color="purple" icon="fa-clock" label="RFQ Pending" value={data.rfq_pending || 0} onClick={() => goToList({ stage: "rfq" })} />
        <InfoBox color="orange" icon="fa-file-invoice" label="Quotation Pending" value={data.quotation_pending || 0} onClick={() => goToList({ stage: "rfq value" })} />
        <InfoBox color="red" icon="fa-triangle-exclamation" label="Lost Inquiries" value={data.lost_enquiries || 0} onClick={() => goToList({ stage: "lost" })} />
        <InfoBox color="teal" icon="fa-calendar-day" label="Today's Activities" value={data.today_activities || 0} onClick={() => goToList({ activity: "today" })} />
        <InfoBox color="indigo" icon="fa-calendar-check" label="Tomorrow's Activities" value={data.tomorrow_activities || 0} onClick={() => goToList({ activity: "tomorrow" })} />

      </div>

      {data.channel_counts && (
        <div className="channel-counts-grid">
          <InfoBox color="blue" icon="fa-building" label="Project" value={data.channel_counts.project || 0} onClick={() => goToList({ channel: "project" })} />
          <InfoBox color="green" icon="fa-store" label="Retail Response" value={data.channel_counts.retail_response || 0} onClick={() => goToList({ channel: "retail response" })} />
          <InfoBox color="orange" icon="fa-calculator" label="Retail Estimate" value={data.channel_counts.retail_estimate || 0} onClick={() => goToList({ channel: "retail estimate" })} />
        </div>
      )}
    </div>
  )
};

const InfoBox = ({ color, icon, label, value, onClick }: any) => (
  <div className={`info-box ${color}`} onClick={onClick} style={{ cursor: "pointer" }}>
    <i className={`fas ${icon}`}></i>
    <strong>{value || 0}</strong>
    <span>{label}</span>
  </div>
);
